import React, {useEffect, useRef, useState} from "react";
import {
    Badge,
    Card,
    CardBody,
    Col,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Media,
    Nav,
    NavItem,
    NavLink,
    Row
} from "reactstrap";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import "../avatarActions.scss";

/** Section IDs for tabs and scroll spy */
export const BUYER_LEAD_SECTION_IDS = [
    "customer-details", "addresses", "membership", "sizing", "orders",
    "abandoned-cart", "support", "feedback", "marketing", "reviews", "social", "ai", "notes",
];

const TAB_LABELS = {
    "customer-details": "Customer Details",
    "addresses": "Addresses",
    "membership": "Membership & Loyalty",
    "sizing": "Sizing & Fit",
    "orders": "Orders & Financials",
    "abandoned-cart": "Abandoned Cart",
    "support": "Support Cases",
    "feedback": "Feedback & Interests",
    "marketing": "Marketing History",
    "reviews": "Reviews & UGC",
    "social": "Social Connections",
    "ai": "AI Usage",
    "notes": "Notes & Audit",
};

const tabs = BUYER_LEAD_SECTION_IDS.map((id) => ({
    id,
    label: TAB_LABELS[id],
    enabled: true,
}));

const BuyerLeadHeader = ({
    avatarUrl,
    fullName,
    leadCode,
    email,
    phone,
    memberSince,
    accountStatus,
    summaryBadges,
    lastUpdated,
    onEdit,
    isEditMode,
    formatDate,
    formatDateTime,
    activeTab,
    setActiveTab,
    scrollToSection,
    onAvatarUpload,
    onAvatarRemove,
    isAvatarUploading,
    canUploadAvatar,
    canRemoveAvatar,
}) => {
    const fileInputRef = useRef(null);
    const isAvatarUploadEnabled = Boolean(onAvatarUpload) && canUploadAvatar !== false;
    const isAvatarRemoveEnabled = Boolean(onAvatarRemove) && canRemoveAvatar !== false;
    const isAvatarPreviewEnabled = Boolean(avatarUrl);
    const canOpenAvatarMenu = isAvatarPreviewEnabled || isAvatarUploadEnabled || isAvatarRemoveEnabled;
    const avatarMenuDisabled = !canOpenAvatarMenu || isAvatarUploading;
    const [isAvatarPreviewOpen, setIsAvatarPreviewOpen] = useState(false);
    const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
    const [tabStartIndex, setTabStartIndex] = useState(0);
    const tabsPerRow = 6;
    const avatarLabel = isAvatarUploading ? "Uploading profile image" : "Change profile image";
    const normalizeBadgeKey = (label = "") =>
        label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
    const badgeIconMap = {
        active: "fa-check-circle",
        inactive: "fa-times-circle",
        "high-value": "fa-star",
        "email-consent": "fa-check",
        "sms-consent": "fa-check",
        "push-disabled": "fa-times",
    };
    const statusValue = accountStatus || "-";
    const normalizedStatus = String(statusValue).toLowerCase();
    const statusClass =
        normalizedStatus === "active"
            ? "is-active"
            : normalizedStatus === "inactive"
                ? "is-inactive"
                : "is-unknown";
    const badges = summaryBadges || [];
    const editLabel = isEditMode ? "View Mode" : "Edit Mode";
    const editIcon = isEditMode ? "fa-eye" : "fa-pencil";
    const editButtonClass = "btn btn-dark btn-sm";
    const avatarSize = 72;

    const handleAvatarPreview = () => {
        if (!isAvatarPreviewEnabled) {
            return;
        }
        setAvatarMenuOpen(false);
        setIsAvatarPreviewOpen(true);
    };

    const handleAvatarClick = () => {
        if (!isAvatarUploadEnabled || isAvatarUploading) {
            return;
        }
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const toggleAvatarMenu = () => {
        if (avatarMenuDisabled) {
            return;
        }
        setAvatarMenuOpen((prev) => !prev);
    };

    const handleAvatarUploadClick = (event) => {
        if (!isAvatarUploadEnabled) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        handleAvatarClick();
    };

    const handleAvatarUploadKeyDown = (event) => {
        if (!isAvatarUploadEnabled) {
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleAvatarClick();
        }
    };

    const handleAvatarChange = (event) => {
        const file = event.target.files?.[0];
        if (file && onAvatarUpload) {
            onAvatarUpload(file);
        }
        if (event.target) {
            event.target.value = "";
        }
    };

    const handleAvatarRemove = () => {
        if (!isAvatarRemoveEnabled || isAvatarUploading) {
            return;
        }
        setAvatarMenuOpen(false);
        if (onAvatarRemove) {
            onAvatarRemove();
        }
    };

    // Tab navigation handlers
    const visibleTabs = tabs.slice(tabStartIndex, tabStartIndex + tabsPerRow);
    const canScrollLeft = tabStartIndex > 0;
    const canScrollRight = tabStartIndex + tabsPerRow < tabs.length;

    const handleScrollLeft = () => {
        setTabStartIndex(Math.max(0, tabStartIndex - tabsPerRow));
    };

    const handleScrollRight = () => {
        setTabStartIndex(Math.min(tabs.length - tabsPerRow, tabStartIndex + tabsPerRow));
    };

    // Auto-scroll to show active tab when it's not visible
    useEffect(() => {
        const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
        if (activeTabIndex !== -1) {
            if (activeTabIndex < tabStartIndex) {
                setTabStartIndex(activeTabIndex);
            } else if (activeTabIndex >= tabStartIndex + tabsPerRow) {
                setTabStartIndex(Math.max(0, activeTabIndex - tabsPerRow + 1));
            }
        }
    }, [activeTab, tabStartIndex, tabsPerRow]);

    return (
        <Row className="buyer-lead-header">
            <Col sm="12">
                <Card className="mb-3">
                    <CardBody>
                        <Row className="align-items-start">
                            <Col md="8">
                                <div className="buyer-lead-header-main">
                                    <div
                                        className={`buyer-lead-avatar-wrapper ${isAvatarUploadEnabled ? "is-editable" : ""}`}
                                        aria-busy={isAvatarUploading || undefined}
                                    >
                                        <Dropdown isOpen={avatarMenuOpen} toggle={toggleAvatarMenu}>
                                            <DropdownToggle
                                                tag="button"
                                                type="button"
                                                className="p-0 border-0 bg-transparent"
                                                style={{lineHeight: 0}}
                                                disabled={avatarMenuDisabled}
                                            >
                                                <Media
                                                    className={`rounded-circle buyer-lead-avatar ${
                                                        isAvatarPreviewEnabled ? "is-previewable" : ""
                                                    }`}
                                                    style={{height: avatarSize, width: avatarSize, objectFit: "cover"}}
                                                    src={avatarUrl}
                                                    alt="buyer-avatar"
                                                    title="Image options"
                                                />
                                            </DropdownToggle>
                                            <DropdownMenu className="avatar-actions-menu">
                                                <DropdownItem
                                                    onClick={handleAvatarPreview}
                                                    disabled={!isAvatarPreviewEnabled}
                                                    className="avatar-actions-item"
                                                >
                                                    <span className="avatar-actions-icon">
                                                        <i className="fa fa-eye"></i>
                                                    </span>
                                                    <span>View image</span>
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={handleAvatarClick}
                                                    disabled={!isAvatarUploadEnabled}
                                                    className="avatar-actions-item"
                                                >
                                                    <span className="avatar-actions-icon">
                                                        <i className="fa fa-camera"></i>
                                                    </span>
                                                    <span>Edit image</span>
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={handleAvatarRemove}
                                                    disabled={!isAvatarRemoveEnabled}
                                                    className="avatar-actions-item is-danger"
                                                >
                                                    <span className="avatar-actions-icon">
                                                        <i className="fa fa-trash"></i>
                                                    </span>
                                                    <span>Delete image</span>
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                        {isAvatarUploadEnabled && (
                                            <>
                                                <div
                                                    className={`buyer-lead-avatar-overlay ${
                                                        isAvatarUploading ? "is-uploading" : ""
                                                    }`}
                                                    aria-hidden="true"
                                                    onClick={handleAvatarUploadClick}
                                                    onKeyDown={handleAvatarUploadKeyDown}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={avatarLabel}
                                                    title={avatarLabel}
                                                >
                                                    <i
                                                        className={`fa ${
                                                            isAvatarUploading ? "fa-spinner fa-spin" : "fa-camera"
                                                        }`}
                                                    ></i>
                                                </div>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    className="buyer-lead-avatar-input"
                                                    onChange={handleAvatarChange}
                                                />
                                            </>
                                        )}
                                    </div>
                                    <div className="buyer-lead-header-info">
                                        <div className="buyer-lead-header-title-row">
                                            <h4 className="mb-0 mr-2">{fullName}</h4>
                                            <span className="text-muted">#{leadCode}</span>
                                            <span
                                                className={`buyer-lead-status-pill buyer-lead-status-inline ${statusClass}`}
                                            >
                                                <span className="buyer-lead-status-dot" aria-hidden="true"></span>
                                                {statusValue}
                                            </span>
                                        </div>
                                        <div className="text-muted buyer-lead-header-meta mt-1">
                                            <span>
                                                <i className="fa fa-envelope mr-1"></i>
                                                {email}
                                            </span>
                                            <span>
                                                <i className="fa fa-phone mr-1"></i>
                                                {phone}
                                            </span>
                                        </div>
                                        <div className="text-muted small mt-1 buyer-lead-header-meta-dates">
                                            <div>
                                                <i className="fa fa-calendar mr-1"></i>
                                                Member since {formatDate(memberSince)}
                                            </div>
                                            <div className="mt-1">
                                                <i className="fa fa-clock-o mr-1"></i>
                                                Last updated: {formatDateTime(lastUpdated)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col md="4" className="buyer-lead-header-actions mt-3 mt-md-0">
                                <button type="button" className={editButtonClass} onClick={onEdit}>
                                    <i className={`fa ${editIcon} mr-1`}></i> {editLabel}
                                </button>
                                <button type="button" className="btn btn-success btn-sm" disabled>
                                    <i className="fa fa-file-pdf-o mr-1"></i> Export PDF
                                </button>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md="12">
                                <div className="d-flex flex-wrap buyer-lead-summary-badges">
                                    {badges.map((badge, index) => {
                                        const badgeKey = normalizeBadgeKey(badge.label);
                                        const iconClass = badgeIconMap[badgeKey];
                                        return (
                                            <Badge
                                                key={`${badge.label}-${index}`}
                                                pill
                                                className={`buyer-lead-summary-badge buyer-lead-summary-badge--${badgeKey}`}
                                            >
                                                {badgeKey === "vip-member" ? (
                                                    <svg
                                                        className="buyer-lead-summary-icon buyer-lead-summary-icon--vip"
                                                        viewBox="0 0 24 24"
                                                        aria-hidden="true"
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M5 16L3 6l5 4 4-6 4 6 5-4-2 10H5zm0 2h14v2H5z"
                                                        />
                                                    </svg>
                                                ) : (
                                                    iconClass && (
                                                        <i
                                                            className={`fa ${iconClass} buyer-lead-summary-icon`}
                                                        ></i>
                                                    )
                                                )}
                                                <span>{badge.label}</span>
                                            </Badge>
                                        );
                                    })}
                                </div>
                            </Col>
                        </Row>
                        {!isEditMode && (
                            <Row className="mt-2">
                                <Col>
                                    <div className="buyer-lead-tabs-wrapper">
                                        {canScrollLeft && (
                                            <button
                                                type="button"
                                                className="buyer-lead-tabs-arrow buyer-lead-tabs-arrow-left"
                                                onClick={handleScrollLeft}
                                                aria-label="Scroll tabs left"
                                            >
                                                <i className="fa fa-chevron-left"></i>
                                            </button>
                                        )}
                                        <Nav tabs className="border-tab buyer-lead-tab buyer-lead-tabs">
                                            {visibleTabs.map((tab) => (
                                                <NavItem key={tab.id}>
                                                    <NavLink
                                                        href="#"
                                                        disabled={!tab.enabled}
                                                        className={activeTab === tab.id ? "active" : ""}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (tab.enabled) {
                                                                setActiveTab(tab.id);
                                                                scrollToSection(tab.id);
                                                            }
                                                        }}
                                                    >
                                                        {tab.label}
                                                    </NavLink>
                                                </NavItem>
                                            ))}
                                        </Nav>
                                        {canScrollRight && (
                                            <button
                                                type="button"
                                                className="buyer-lead-tabs-arrow buyer-lead-tabs-arrow-right"
                                                onClick={handleScrollRight}
                                                aria-label="Scroll tabs right"
                                            >
                                                <i className="fa fa-chevron-right"></i>
                                            </button>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </CardBody>
                </Card>
            </Col>
            {isAvatarPreviewOpen && isAvatarPreviewEnabled && (
                <Lightbox mainSrc={avatarUrl} onCloseRequest={() => setIsAvatarPreviewOpen(false)} />
            )}
        </Row>
    );
};

export default BuyerLeadHeader;
